/**
 * Hand reorder (long-press drag) — Bulle
 * - Long press a card in your hand to enter "reorder mode"
 * - Drag left/right to change order
 * - Updates myHandPile (array of card tokens) and re-renders
 *
 * Designed to be safe:
 * - Normal tap still plays a card
 * - Only activates if the press lasts LONG_PRESS_MS
 */
(function(){
  const LONG_PRESS_MS = 450;

  // 🔧 PROTECTION: Flag to prevent sync overwrites during reorder
  window.__handReorderInProgress = false;

  function qs(sel, root=document){ return root.querySelector(sel); }

  let pressTimer = null;
  let dragging = null;
  let placeholder = null;
  let startX = 0;
  let startY = 0;
  let pointerId = null;
  let didDrag = false;

  // Pending (before long-press triggers) — used to cancel reorder when user scrolls
  let pendingPointerId = null;
  let pendingStartX = 0;
  let pendingStartY = 0;
  
  // 🔧 CRITICAL: Make these globally accessible for debugging
  if(typeof window !== 'undefined') {
    window.__reorderState = {
      get dragging() { return dragging; },
      get pointerId() { return pointerId; },
      get didDrag() { return didDrag; }
    };
  }

  function getHandContainer(){
    return document.getElementById("handCardsContainer");
  }

  function cardElems(container){
    return Array.from(container.querySelectorAll(".handCard"));
  }

  function tokenOf(el){
    return el?.dataset?.tok || null;
  }

  function indexFromPoint(container, x, y){
    const cards = cardElems(container).filter(el => el !== dragging && el !== placeholder);
    if(cards.length === 0) return 0;

    // Find closest card center on X axis (hand is horizontal scroll on mobile)
    let bestIdx = cards.length;
    let bestDist = Infinity;
    for(let i=0;i<cards.length;i++){
      const r = cards[i].getBoundingClientRect();
      const cx = r.left + r.width/2;
      const dist = Math.abs(cx - x);
      if(dist < bestDist){
        bestDist = dist;
        bestIdx = i;
      }
    }
    // Insert before/after based on side
    const r = cards[bestIdx].getBoundingClientRect();
    const insertAfter = x > (r.left + r.width/2);
    return Math.min(cards.length, bestIdx + (insertAfter ? 1 : 0));
  }

  function beginDrag(cardEl, pseudoEvent){
    const container = getHandContainer();
    if(!container || !cardEl || !cardEl.parentNode) return;

    // 🔧 Set protection flag
    window.__handReorderInProgress = true;
    console.log("🎯 Hand reorder started", {pointerId: pseudoEvent.pointerId});

    didDrag = false;
    dragging = cardEl;
    pointerId = pseudoEvent.pointerId;

    const rect = dragging.getBoundingClientRect();
    startX = pseudoEvent.clientX;
    startY = pseudoEvent.clientY;

    // placeholder keeps layout
    placeholder = document.createElement("div");
    placeholder.className = "handCard handCard--placeholder";
    placeholder.style.width = rect.width + "px";
    placeholder.style.height = rect.height + "px";
    placeholder.style.opacity = "0.25";
    placeholder.style.borderRadius = getComputedStyle(dragging).borderRadius || "16px";
    placeholder.style.border = "2px dashed rgba(255,255,255,0.25)";

    dragging.parentNode.insertBefore(placeholder, dragging);
    dragging.classList.add("handCard--dragging");

    // lift card (fixed)
    dragging.style.position = "fixed";
    dragging.style.left = rect.left + "px";
    dragging.style.top = rect.top + "px";
    dragging.style.width = rect.width + "px";
    dragging.style.height = rect.height + "px";
    dragging.style.zIndex = "9999";
    dragging.style.pointerEvents = "none";
    dragging.style.touchAction = "none";

    // prevent scroll while dragging
    document.body.classList.add("handReorderDragging");

    // Note: setPointerCapture may not work with pseudo-event, but that's OK
    // The drag will still work via pointermove/pointerup handlers
    try { 
      if(dragging.setPointerCapture) {
        dragging.setPointerCapture(pointerId);
        console.log("✅ Pointer captured");
      }
    } catch(err) {
      console.log("⚠️ setPointerCapture failed (non-critical):", err.message);
    }

    // haptic feedback if available
    try { if(navigator.vibrate) navigator.vibrate(12); } catch(_){}
  }

  function moveDrag(e){
    if(!dragging) {
      console.log("⚠️ moveDrag called but no dragging element");
      return;
    }
    
    // 🔧 FIX: Log but don't block if pointerId mismatch
    // Some browsers/devices change pointerId during drag
    if(e.pointerId !== pointerId) {
      console.log("ℹ️ moveDrag: pointerId changed", {event: e.pointerId, original: pointerId});
      // Update to new pointerId
      pointerId = e.pointerId;
    }

    // While dragging, prevent scrolling/selection
    try{ e.preventDefault(); }catch(_){ }

    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    if(Math.abs(dx) + Math.abs(dy) > 6) {
      if(!didDrag) console.log("🎯 Drag movement detected", {dx, dy});
      didDrag = true;
    }

    dragging.style.transform = `translate(${dx}px, ${dy}px)`;

    const container = getHandContainer();
    if(!container) return;

    const idx = indexFromPoint(container, e.clientX, e.clientY);
    const cards = cardElems(container).filter(el => el !== dragging && el !== placeholder);

    if(idx >= cards.length){
      container.appendChild(placeholder);
    } else {
      container.insertBefore(placeholder, cards[idx]);
    }
  }

  function endDrag(e){
    if(!dragging) {
      console.log("⚠️ endDrag called but no dragging");
      return;
    }

    try{ e.preventDefault(); }catch(_){ }

    const container = getHandContainer();
    const tok = tokenOf(dragging);

    // Compute new order from DOM placeholder.
    // IMPORTANT: In some edge cases, a card can be missing its metadata and therefore
    // not be rendered (cardByToken() -> null). In that situation, a strict length check
    // would prevent reordering entirely. Instead, we reorder the visible tokens and
    // keep any "non-rendered" tokens at the end, preserving their relative order.
    if(container && tok && typeof myHandPile !== "undefined" && Array.isArray(myHandPile)){
      // DOM order excluding the dragged element (which is position:fixed).
      const domEls = cardElems(container).filter(el => el !== dragging);
      const phIndex = domEls.indexOf(placeholder);

      // Visible tokens in their current DOM order (excluding placeholder).
      const visibleOrdered = domEls
        .filter(el => el !== placeholder)
        .map(tokenOf)
        .filter(Boolean);

      // Insert dragged token at placeholder index.
      const newVisible = visibleOrdered.slice();
      newVisible.splice(Math.max(0, phIndex), 0, tok);

      // Keep any tokens that are currently in myHandPile but not rendered.
      const visibleSet = new Set(newVisible);
      const rest = myHandPile.filter(t => !visibleSet.has(t));

      const newOrder = newVisible.concat(rest);
      
      // 🔧 CRITICAL FIX: Update myHandPile FIRST
      myHandPile = newOrder;

      // 🔧 CRITICAL FIX: Wait for Supabase persist BEFORE rendering
      // This ensures the DB is updated before any realtime event can overwrite
      const doPersist = async () => {
        if(typeof window.persistHandOrder === "function") {
          try {
            await window.persistHandOrder(newOrder);
            console.log("✅ Hand order persisted:", newOrder.slice(0, 3));
          } catch(err) {
            console.error("❌ Erreur persistHandOrder:", err);
          }
        } else {
          console.warn("⚠️ persistHandOrder not available");
        }
        
        // Re-render AFTER persistence
        if(typeof renderHandCards === "function") renderHandCards();
        
        // 🔧 Cleanup drag AFTER render (so the new order is visible)
        const hadDrag = didDrag;
        cleanupDrag('success'); // Pass 'success' to avoid "cancelled" log
        if(hadDrag) {
          setTimeout(() => { didDrag = false; }, 0);
        } else {
          didDrag = false;
        }
        
        // 🔧 Clear protection flag AFTER everything is done
        setTimeout(() => {
          window.__handReorderInProgress = false;
          console.log("✅ Hand reorder complete, protection cleared");
        }, 100);
      };
      
      doPersist();
      return; // Don't call cleanupDrag here, it's done inside doPersist
    }

    // If we didn't do a reorder, still cleanup
    cleanupDrag();
    didDrag = false;
  }

  function cleanupDrag(reason = 'cancel'){
    if(!dragging) return;

    // 🔧 Clear protection flag
    window.__handReorderInProgress = false;
    
    if(reason === 'cancel') {
      console.log("🔧 Hand reorder cancelled");
    }

    document.body.classList.remove("handReorderDragging");

    dragging.classList.remove("handCard--dragging");
    dragging.style.position = "";
    dragging.style.left = "";
    dragging.style.top = "";
    dragging.style.width = "";
    dragging.style.height = "";
    dragging.style.zIndex = "";
    dragging.style.pointerEvents = "";
    dragging.style.touchAction = "";
    dragging.style.transform = "";

    if(placeholder && placeholder.parentNode) placeholder.parentNode.removeChild(placeholder);

    dragging = null;
    placeholder = null;
    pointerId = null;
    // IMPORTANT: do NOT reset didDrag here.
    // A click event can fire after pointerup; we rely on onClickCapture()
    // to suppress that click when a drag actually happened.
  }

  function onPointerDown(e){
    const container = getHandContainer();
    if(!container) return;
    const cardEl = e.target.closest?.(".handCard");
    if(!cardEl || !container.contains(cardEl)) return;

    // Ignore right click / pen button
    if(e.button && e.button !== 0) return;

    // If user is selecting a target, etc., don't interfere
    if(document.body.classList.contains("targetingMode")) return;

    clearTimeout(pressTimer);
    
    // 🔧 CAPTURE pointer info BEFORE setTimeout (event will be stale after 450ms)
    const capturedPointerId = e.pointerId;
    const capturedClientX = e.clientX;
    const capturedClientY = e.clientY;
    
    pendingPointerId = capturedPointerId;
    pendingStartX = capturedClientX;
    pendingStartY = capturedClientY;
    
    pressTimer = setTimeout(() => {
      // Card may have been removed from DOM during the long-press delay
      if(!cardEl.parentNode || !cardEl.isConnected) {
        pressTimer = null;
        return;
      }
      // Create a pseudo-event object with captured values
      const pseudoEvent = {
        pointerId: capturedPointerId,
        clientX: capturedClientX,
        clientY: capturedClientY,
        preventDefault: () => {},
        stopPropagation: () => {}
      };
      beginDrag(cardEl, pseudoEvent);
    }, LONG_PRESS_MS);
  }

  function onPointerMove(e){
    // If user starts scrolling/moving before long-press, cancel the timer.
    if(!dragging && pressTimer && e.pointerId === pendingPointerId){
      const dx = Math.abs(e.clientX - pendingStartX);
      const dy = Math.abs(e.clientY - pendingStartY);
      if(dx > 10 || dy > 10){
        clearTimeout(pressTimer);
        pressTimer = null;
        pendingPointerId = null;
      }
      return;
    }

    // 🔧 FIX: If dragging, process the move even if pointerId doesn't match
    // (some browsers change pointerId during drag)
    if(dragging) {
      moveDrag(e);
    }
  }

  function onPointerUp(e){
    clearTimeout(pressTimer);
    pressTimer = null;
    pendingPointerId = null;
    
    if(dragging) {
      console.log("🔵 onPointerUp during drag", {pointerId: e.pointerId, original: pointerId});
      // 🔧 FIX: Always end drag on pointerup, even if pointerId doesn't match
      // (some browsers change pointerId)
      endDrag(e);
    }
  }

  function onPointerCancel(e){
    clearTimeout(pressTimer);
    pressTimer = null;
    pendingPointerId = null;
    
    if(dragging) {
      console.log("🔴 onPointerCancel called", {pointerId: e.pointerId, expected: pointerId});
    }
    
    if(dragging) cleanupDrag();
  }

  // Block click after a drag, so it doesn't "play" the card
  function onClickCapture(e){
    if(!didDrag) return;
    const cardEl = e.target.closest?.(".handCard");
    if(cardEl){
      e.preventDefault();
      e.stopPropagation();
      didDrag = false;
    }
  }

  function injectCSS(){
    const css = `
      body.handReorderDragging { touch-action: none !important; overscroll-behavior: none; }
      .handCard--dragging { box-shadow: 0 12px 30px rgba(0,0,0,0.45); }
      .handCard--placeholder { background: rgba(255,255,255,0.04); }
    `;
    const style = document.createElement("style");
    style.textContent = css;
    document.head.appendChild(style);
  }

  function init(){
    injectCSS();
    document.addEventListener("pointerdown", onPointerDown, {passive:false});
    document.addEventListener("pointermove", onPointerMove, {passive:false});
    document.addEventListener("pointerup", onPointerUp, {passive:false});
    document.addEventListener("pointercancel", onPointerCancel, {passive:false});
    document.addEventListener("click", onClickCapture, true);
  }

  if(document.readyState === "loading"){
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
